import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { FaEye } from "react-icons/fa";
import { Files } from "lucide-react";
import { useLocation, useNavigate } from 'react-router-dom';
import ReportFilters from './ReportFilters';
import AttemptDropdown from './AttemptDropdown';
import TableEmptyRow from '../shared/TableEmptyRow';
import Checkbox from "@mui/material/Checkbox";
import axios from 'axios';

const pageSize = 10;

const stringOperators = [
  { value: 'contains', key: 'Contains' },
  { value: 'equal', key: 'Equal' },
  { value: 'notequal', key: 'Not Equal' },
  { value: 'startswith', key: 'Starts With' },
  { value: 'endswith', key: 'Ends With' },
  { value: 'isempty', key: 'Is Empty' },
  { value: 'isnotempty', key: 'Is Not Empty' },
];

const numberOperators = [
  { value: 'equal', key: 'Equal' },
  { value: 'notequal', key: 'Not Equal' },
  { value: 'greaterthan', key: 'Greater Than' },
  { value: 'lessthan', key: 'Less Than' },
  { value: 'greaterthanorequal', key: 'Greater Than Or Equal' },
  { value: 'lessthanorequal', key: 'Less Than Or Equal' },
];

const employeeCourseQueryBuilderColumns = [
  { field: 'title', label: 'Title', type: 'string', operators: stringOperators },
  { field: 'exam_type', label: 'Assessment Type', type: 'string', operators: stringOperators },
  { field: 'attempt_count', label: 'Attempt Count', type: 'number', operators: numberOperators },
  { field: 'pass_percent', label: 'Pass Percentage', type: 'number', operators: numberOperators },
  {
    field: 'status',
    label: 'Status',
    type: 'string',
    operators: [{ value: 'equal', key: 'Equal' }],
    values: ['Passed', 'Failed'],
  },
];

const EmployeeCoursesReport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const employeeGuid = params.get('employeeGuid');
  const employeePage = params.get('employeePage') || 1;
  const [selectedRows, setSelectedRows] = useState([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterRule, setFilterRule] = useState(null);
  const [selectedAttempt, setSelectedAttempt] = useState("all");
  const [reports, setReports] = useState([]);
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');

  const fetchCourseReports = async (pageNum = 1, pageSizeOverride = pageSize) => {
    if (!employeeGuid) {
      setReports([]);
      setTotalCount(0);
      setEmployee(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const query = new URLSearchParams({
        employee_guid: employeeGuid,
        search: searchTerm,
        page: pageNum,
        page_size: pageSizeOverride,
        attempt: selectedAttempt,
      });

      if (filterRule && filterRule.rules?.length) {
        query.append('filters', JSON.stringify(filterRule));
      }

      const response = await axios.get(`/reports/employee-wise/courses/?${query.toString()}`, {
        withCredentials: true
      });
      const data = response.data;

      if (response.status < 200 || response.status >= 300) {
        alert(data.error || 'Failed to load course reports');
        setReports([]);
        setTotalCount(0);
        return;
      }

      setReports(data.reports || []);
      setEmployee(data.employee || null);
      setTotalCount(data.total_count || 0);
      setPage(pageNum);
    } catch (error) {
      console.error('Error fetching employee course reports:', error);
      setReports([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourseReports(1);
  }, [employeeGuid, searchTerm, filterRule, selectedAttempt]);

  const handleGoBack = () => {
    navigate(`/report?tab=employee&employeePage=${employeePage}`);
  };

  const handleExportCSV = async () => {
    try {
      const query = new URLSearchParams({
        employee_guid: employeeGuid,
        search: searchTerm,
        page: 1,
        page_size: Math.max(totalCount, pageSize),
        attempt: selectedAttempt,
      });

      if (filterRule && filterRule.rules?.length) {
        query.append('filters', JSON.stringify(filterRule));
      }

      let exportReports = selectedRows;

      if (exportReports.length === 0) {
        const response = await axios.get(`/reports/employee-wise/courses/?${query.toString()}`, {
          withCredentials: true
        });
        const data = response.data;

        if (response.status < 200 || response.status >= 300) {
          alert(data.error || 'Failed to export data.');
          return;
        }

        exportReports = data.reports || [];
      }

      if (exportReports.length === 0) {
        alert('No data to export!');
        return;
      }

      const headers = ['Title', 'Assessment Type', 'Attempt Count', 'Pass %', 'Status'];
      const rows = exportReports.map((row) => [
        row.title || '',
        row.exam_type || '',
        row.userAttemptCount ?? row.attempt_count ?? '',
        row.pass_percent != null ? `${Number(row.pass_percent).toFixed(2)}%` : '-',
        row.status || '',
      ]);

      const csvContent =
        'data:text/csv;charset=utf-8,' +
        [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');

      const link = document.createElement('a');
      link.href = encodeURI(csvContent);
      link.download = `${employee?.employee_id || 'employee'}_course_reports.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting employee course reports:', error);
      alert('Failed to export data.');
    }
  };

  const fetchAllCourseReports = async () => {
    const query = new URLSearchParams({
      employee_guid: employeeGuid,
      search: searchTerm,
      page: 1,
      page_size: Math.max(totalCount, pageSize),
      attempt: selectedAttempt,
    });

    if (filterRule && filterRule.rules?.length) {
      query.append('filters', JSON.stringify(filterRule));
    }

    const response = await axios.get(`/reports/employee-wise/courses/?${query.toString()}`, {
      withCredentials: true
    });
    const data = response.data;

    if (response.status < 200 || response.status >= 300) {
      throw new Error(data.error || 'Failed to load course reports');
    }

    return data.reports || [];
  };

  const handleBulkDownload = async () => {
    try {
      setBulkDownloading(true);

      const reportsToDownload =
        selectedRows.length > 0 ? selectedRows : await fetchAllCourseReports();

      const downloadableReports = reportsToDownload.filter((row) => {
        const status = String(row.status || "").trim().toLowerCase();
        return row.ee_guid && ["passed", "failed"].includes(status);
      });

      if (downloadableReports.length === 0) {
        alert("No reports available to download");
        return;
      }

      const employees = downloadableReports
        .map((row) => ({
          ee_id: row.ee_guid,
          name: `${employee?.name || "Employee"}_${row.title || "Report"}`,
          exam_type_id: row.exam_type_id || (row.exam_type === "Competency Review" ? 1 : 2),
        }));

      if (employees.length === 0) {
        alert("No reports available to download");
        return;
      }
      console.log(employees,"yyyyyyyyyyyyyyyyyyyyyyyyyyyy")

      const response = await axios.post(
        "/reports/bulk-report/",
        {
          exam_type_id: employees[0]?.exam_type_id,
          employees,
        },
        {
          headers: { "Content-Type": "application/json" },
          responseType: "blob",
          withCredentials: true,
        }
      );
      console.log(JSON.stringify({
          exam_type_id: employees[0]?.exam_type_id,
          employees,
        }),"ppppppppppppppppppp");

      console.log(response,"uuuuuuuuuuuuuuuuuuu")

      const blob = response.data;
      console.log("Blob Size:", blob.size);
      console.log("Blob Type:", blob.type);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${employee?.employee_id || "employee"}_course_reports.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Bulk download error:", error);
      alert("Failed to download reports");
    } finally {
      setBulkDownloading(false);
    }
  };

  const handleViewReport = async (row) => {
    if (!row.ee_guid) {
      alert("Report is not available for this record");
      return;
    }

    try {
      const examTypeId = row.exam_type === "Competency Review" ? 1 : 2;
      const response = await axios.get(`/reports/exam-report/${row.ee_guid}/${examTypeId}/`, {
        withCredentials: true
      });
      const data = response.data;

      if (response.status < 200 || response.status >= 300) {
        alert(data.error || "Failed to fetch report");
        return;
      }

      const generateResponse = await axios.post(
        `/reports/exam-report/${row.ee_guid}/${examTypeId}/`,
        {},
        { withCredentials: true }
      );
      const pdfData = generateResponse.data;

      if (!pdfData.success) {
        alert(pdfData.error || "Failed to generate PDF");
        return;
      }

      const byteCharacters = atob(pdfData.fileBase64);
      const byteNumbers = [...byteCharacters].map((char) => char.charCodeAt(0));
      const blob = new Blob([new Uint8Array(byteNumbers)], { type: "application/pdf" });

      setPdfUrl(URL.createObjectURL(blob));
      setPdfDialogOpen(true);
    } catch (error) {
      console.error("Error fetching report:", error);
      alert("Failed to fetch report");
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const renderPageNumbers = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (page <= 3) {
      pages.push(1, 2, 3, '...', totalPages);
    } else if (page >= totalPages - 2) {
      pages.push(1, '...', totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
    }
    return pages;
  };

  const handlePageChange = (newPage) => {
    fetchCourseReports(newPage);
  };
  const toggleRow = (row) => {
    setSelectedRows(prev => {
      const exists = prev.some(x => x.ee_guid === row.ee_guid);

      if (exists) {
        return prev.filter(x => x.ee_guid !== row.ee_guid);
      }

      return [...prev, row];
    });
  };
  const handleSelectAll = async (event) => {

    const checked = event.target.checked;

    if (!checked) {
      setSelectedRows([]);
      return;
    }

    try {

      const allReports = await fetchAllCourseReports();

      setSelectedRows(allReports);

    }
    catch (err) {
      console.error(err);
    }

  };

  const allSelected = React.useMemo(() => {
    return totalCount > 0 && selectedRows.length === totalCount;
  }, [selectedRows, totalCount]);
  const renderReports = () => {
    if (loading) {
      return (
        <div className="pd-table-loading">
          <div className="pd-table-loading-animation">
            <img className="pd-table-loading-video" src="/images/breath1.gif" alt="Loading reports..." />
            <p>Loading reports...</p>
          </div>
        </div>
      );
    }

    return (
      <>
        <div className={reports.length === 0 ? "" : "d-none d-md-block"} style={{ overflowX: "auto", scrollbarWidth: "thin", minHeight: "200px", maxHeight: '400px', borderRadius: '6px' }}>
          <table className="pd-table" style={{ width: "100%" }}>
            <thead>
              <tr>
                <th style={{ width: "1%", whiteSpace: "nowrap", padding: "0 4px" }}>
                  <Checkbox
                    checked={allSelected}
                    indeterminate={
                      selectedRows.length > 0 &&
                      selectedRows.length < totalCount
                    }
                    onChange={handleSelectAll}
                    
                  />
                </th>
                <th width="200px">Title</th>
                <th width="100px">Assessment Type</th>
                <th width="100px">Attempt Count</th>
                <th width="100px">Pass Percentage</th>
                <th width="100px">Status</th>
                <th width="150px">Actions</th>
              </tr>
            </thead>
            <tbody>
              {reports.length === 0 ? (
                <TableEmptyRow colSpan={7} message="No reports found" />
              ) : reports.map((row, index) => (
                <tr key={row.ee_guid || `${row.title}-${index}`}>
                 <td style={{ width: "1%", whiteSpace: "nowrap", padding: "0 4px" }}>
                    <Checkbox
                      checked={selectedRows.some(
                        x => x.ee_guid === row.ee_guid
                      )}
                      onChange={() => toggleRow(row)}
                    />
                  </td>
                  <td>{row.title}</td>
                  <td>{row.exam_type}</td>
                  <td>{row.userAttemptCount ?? row.attempt_count ?? "-"}</td>
                  <td>
                    {row.pass_percent != null
                      ? `${Number(row.pass_percent).toFixed(2)}%`
                      : "-"}
                  </td>
                  <td>{row.status}</td>
                  <td style={{ verticalAlign: 'middle', textAlign: 'left', paddingLeft: '8px' }}>
                    <button onClick={() => handleViewReport(row)} className="btn-no-padding" title="View Report">
                      <FaEye className="action-icon" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={reports.length === 0 ? "d-none" : "d-md-none pr-1"} style={{ overflowX: "auto", scrollbarWidth: "thin", minHeight: "200px", maxHeight: '400px' }}>
          {reports.map((row, index) => (
            <div key={row.ee_guid || `${row.title}-${index}`} className="card mb-3 glass-card border-0">
              <div className="card-body">
                <h5 className="card-title d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <Checkbox
                      checked={selectedRows.some(
                        (x) => x.ee_guid === row.ee_guid
                      )}
                      onChange={() => toggleRow(row)}
                      size="small"
                      sx={{ p: 0, mr: 1 }}
                    />
                    {row.title}
                  </div>

                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleViewReport(row)}
                  >
                    <FaEye className="action-icon" />
                  </IconButton>
                </h5>
                <p className="mb-1"><strong>Assessment Type:</strong> {row.exam_type}</p>
                <p className="mb-1"><strong>Attempt Count:</strong> {row.userAttemptCount ?? row.attempt_count ?? "-"}</p>
                <p className="mb-1"><strong>Pass %:</strong> {Number(row.pass_percent).toFixed(2)}%</p>
                <p className="mb-0"><strong>Status:</strong> {row.status}</p>
              </div>
            </div>
          ))}
        </div>

        {reports.length > 0 && <Box className="pd-pagination">
          <button
            className="btn-negative"
            disabled={page === 1}
            onClick={() => handlePageChange(page - 1)}
          >
            Previous
          </button>

          <div className="pd-page-numbers">
            {renderPageNumbers().map((pageNum, index) => (
              <button
                key={index}
                disabled={pageNum === "..."}
                onClick={() =>
                  typeof pageNum === "number" && handlePageChange(pageNum)
                }
                className={`pg-type-btn ${pageNum === page ? "active" : ""}`}
              >
                {pageNum}
              </button>
            ))}
          </div>

          <button
            className="btn-negative"
            disabled={page === totalPages || totalCount === 0}
            onClick={() => handlePageChange(page + 1)}
          >
            Next
          </button>
        </Box>}
      </>
    );
  };

  return (
    <div className="bgImage">
      <section className="MainSectionTop mt-3">
        <div className="CourseBoxWidth">
          <Box className="CourseBox" sx={{ pl: 3, pr: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <button
                type="button"
                className="pd-back-button"
                onClick={handleGoBack}
                aria-label="Go back"
              >
                <i className="fa fa-arrow-circle-left" aria-hidden="true"></i>
              </button>

              <Typography variant="h6">Course Reports</Typography>
            </Box>

            <Card sx={{ background: '#fff', borderRadius: 2, boxShadow: 2, mt: 2 }}>
              <CardContent>
                {employee && (
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                    {employee.name} ({employee.employee_id})
                  </Typography>
                )}

                <ReportFilters
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  setFilterRule={setFilterRule}
                  searchPlaceholder="Search by Title, Assessment Type, Pass Percentage, Status"
                  queryBuilderColumns={employeeCourseQueryBuilderColumns}
                  handleExportCSV={handleExportCSV}
                  showReportButton={false}
                  showExportCSV
                  extraActions={
                    <>
                      <AttemptDropdown
                        value={selectedAttempt}
                        onChange={setSelectedAttempt}
                        latestLabel="Latest Attempts"
                      />
                      <button
                        onClick={handleBulkDownload}
                        disabled={bulkDownloading}
                        className="btn"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          opacity: bulkDownloading ? 0.7 : 1,
                          cursor: bulkDownloading ? "not-allowed" : "pointer",
                        }}
                      >
                        <Files size={20} />
                        {bulkDownloading
                          ? "Downloading..."
                          : selectedRows.length > 0
                            ? `Download Selected (${selectedRows.length})`
                            : "Bulk Download All"}
                      </button>
                    </>
                  }
                />

                {renderReports()}
              </CardContent>
            </Card>
          </Box>
        </div>
      </section>

      <Dialog open={pdfDialogOpen} onClose={() => setPdfDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Report
          <IconButton onClick={() => setPdfDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          {pdfUrl && (
            <iframe
              src={pdfUrl}
              title="Employee Course Report"
              width="100%"
              height="800px"
              style={{ border: "none" }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeCoursesReport;
